import { useState } from "react";
import { Radar, Loader2, Mail, Target } from "lucide-react";
import { toast } from "sonner";
import { startRun } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { SCOUT_MISSIONS } from "@/components/scout/missions";

export const RunScout = ({ activeRun, onStarted, schedule }) => {
  const [brief, setBrief] = useState("");
  const [mission, setMission] = useState(null);
  const [emailDigest, setEmailDigest] = useState(false);
  const [busy, setBusy] = useState(false);
  const running = activeRun?.status === "running";

  const pickMission = (m) => {
    if (mission === m.key) {
      setMission(null);
      setBrief("");
      return;
    }
    setMission(m.key);
    setBrief(m.brief);
  };

  const onBrief = (e) => {
    setBrief(e.target.value);
    setMission(null);
  };

  const go = async () => {
    setBusy(true);
    try {
      const run = await startRun(brief || null, emailDigest);
      onStarted(run);
      toast.success("Scout dispatched", { description: "Searching the web — usually 1–3 minutes." });
    } catch (err) {
      toast.error(extractApiError(err, "Could not start run."));
    } finally {
      setBusy(false);
    }
  };

  const next = schedule?.next_run
    ? new Date(schedule.next_run).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        timeZone: schedule.timezone || undefined,
      })
    : null;

  return (
    <section data-testid="scout-run" className="border border-slate-200 bg-slate-50 p-6">
      <div className="text-xs tracking-tight text-[var(--accent)] mb-2 flex items-center gap-2">
        <Radar className="w-3.5 h-3.5" /> Run Scout Now
      </div>
      <div className="mb-3">
        <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1.5">
          <Target className="w-3 h-3" /> One-click missions
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="scout-missions">
          {SCOUT_MISSIONS.map((m) => (
            <button
              key={m.key}
              type="button"
              data-testid={`scout-mission-${m.key}`}
              aria-pressed={mission === m.key}
              onClick={() => pickMission(m)}
              title={m.pillar}
              className={`px-3 py-1.5 text-xs border transition-colors ${
                mission === m.key
                  ? "bg-[#0A192F] border-[#0A192F] text-white"
                  : "bg-white border-slate-300 text-slate-600 hover:border-[#0A192F] hover:text-[#0A192F]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <textarea
        data-testid="scout-brief-input"
        rows={3}
        value={brief}
        onChange={onBrief}
        placeholder="Optional brief — e.g. 'Georgia theologians who debate church-and-state publicly and have a faculty email'. Leave blank for the default mission."
        className="w-full bg-white border border-slate-300 focus:border-[var(--accent)] px-4 py-3 text-sm outline-none resize-y"
      />
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          data-testid="scout-email-digest-toggle"
          checked={emailDigest}
          onChange={(e) => setEmailDigest(e.target.checked)}
          className="w-4 h-4 accent-[var(--accent)]"
        />
        <Mail className="w-3.5 h-3.5" /> Also email me this run&apos;s digest
      </label>
      <button
        type="button"
        data-testid="scout-run-button"
        onClick={go}
        disabled={busy || running}
        className="mt-4 inline-flex items-center gap-2 bg-[var(--accent)] hover:bg-[#3b7bd0] disabled:opacity-50 text-white px-5 py-3 text-sm font-medium transition-colors"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
        {running ? "Scout is searching…" : "Dispatch scout"}
      </button>
      {next && (
        <p data-testid="scout-next-scheduled" className="mt-4 text-xs text-slate-500">
          Automatic run Tue + Wed (3 candidates) · next {next} ({schedule.timezone}) → digest emailed to atfq.pcast@gmail.com
        </p>
      )}
    </section>
  );
};

export default RunScout;
